import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
  CircularProgress,
  Avatar,
} from '@mui/material';
import { alpha, useTheme, Theme } from '@mui/material/styles';
import { Add, Delete, Edit, PhotoCamera } from '@mui/icons-material';
import { customerService } from '../services/customer.service';
import storageService from '../services/storage.service';
import { Customer, CustomerTypeMap } from '../types';

const buildSurface = (theme: Theme) => ({
  borderRadius: 3,
  border: `1px solid ${alpha(theme.palette.primary.light, 0.22)}`,
  backgroundImage: `linear-gradient(160deg, ${alpha(theme.palette.background.paper, 0.95)}, ${alpha(
    theme.palette.primary.dark,
    0.22,
  )})`,
  boxShadow: `0 18px 32px ${alpha(theme.palette.primary.dark, 0.26)}`,
  backdropFilter: 'blur(12px)',
});

const CustomerManagement: React.FC = () => {
  const theme = useTheme<Theme>();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Omit<Customer, 'id' | 'createdAt' | 'updatedAt'>>({
    name: '',
    type: 'client',
    contact: '',
    phone: '',
    email: '',
    address: '',
  });
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const data = await customerService.getAll();
      setCustomers(data);
      setError('');
    } catch (err) {
      setError('거래처 정보를 불러올 수 없습니다.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (customer?: Customer) => {
    if (customer) {
      setEditingId(customer.id || null);
      setFormData({
        name: customer.name,
        type: customer.type,
        contact: customer.contact,
        phone: customer.phone,
        email: customer.email || '',
        address: customer.address || '',
      });
      setLogoPreview(customer.logoThumbnail || customer.logo || '');
    } else {
      setEditingId(null);
      setFormData({
        name: '',
        type: 'client',
        contact: '',
        phone: '',
        email: '',
        address: '',
      });
      setLogoPreview('');
    }
    setLogoFile(null);
    setOpenDialog(true);
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    try {
      let customerData = { ...formData };
      
      if (editingId) {
        // 로고 업로드 (있는 경우)
        if (logoFile) {
          const { logo, logoThumbnail } = await storageService.uploadCustomerLogo(logoFile, editingId);
          customerData = { ...customerData, logo, logoThumbnail };
        }
        await customerService.update(editingId, customerData);
      } else {
        // 신규 등록
        const newId = await customerService.create(customerData);
        
        // 로고 업로드 후 업데이트 (있는 경우)
        if (logoFile && newId) {
          const { logo, logoThumbnail } = await storageService.uploadCustomerLogo(logoFile, newId);
          await customerService.update(newId, { logo, logoThumbnail });
        }
      }
      
      await loadCustomers();
      setOpenDialog(false);
      setLogoFile(null);
      setLogoPreview('');
    } catch (err) {
      setError('거래처 저장 중 오류가 발생했습니다.');
      console.error(err);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await customerService.delete(id);
      await loadCustomers();
      setDeleteConfirm(null);
    } catch (err) {
      setError('거래처 삭제에 실패했습니다.');
      console.error(err);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 320, p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: 3, md: 4 },
        p: { xs: 2, md: 3 },
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 1.5, md: 2 }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}>
            거래처 관리
          </Typography>
          <Typography variant="body2" sx={{ color: alpha(theme.palette.text.secondary, 0.78) }}>
            고객사와 협력사 정보를 유지 관리하고 연락처를 최신으로 유지하세요.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
          sx={{ px: 3.5, py: 1.1, fontWeight: 600, letterSpacing: '0.06em' }}
        >
          거래처 추가
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'repeat(2, 1fr)',
            lg: 'repeat(3, 1fr)'
          },
          gap: 2.5
        }}
      >
        {customers.map((customer) => (
          <Card key={customer.id} sx={{ ...buildSurface(theme), height: '100%' }}>
              <CardContent sx={{ p: { xs: 2.5, md: 3 }, display: 'flex', gap: 2.5 }}>
                {/* 로고 */}
                <Avatar
                  src={customer.logoThumbnail || customer.logo}
                  sx={{ 
                    width: 80, 
                    height: 80, 
                    border: `2px solid ${alpha(theme.palette.primary.light, 0.3)}` 
                  }}
                >
                  {!customer.logo && customer.name.charAt(0)}
                </Avatar>
                
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                    <Typography variant="h6" sx={{ fontWeight: 700 }} noWrap>
                      {customer.name}
                    </Typography>
                    <Chip
                      label={CustomerTypeMap[customer.type]}
                      color={customer.type === 'client' ? 'primary' : 'secondary'}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </Stack>

                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} noWrap>
                    담당자: {customer.contact}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    연락처: {customer.phone}
                  </Typography>
                  {customer.email && (
                    <Typography variant="body2" color="text.secondary" noWrap>
                      이메일: {customer.email}
                    </Typography>
                  )}
                </Box>
              </CardContent>
              
              <Stack direction="row" spacing={1} sx={{ p: 2, pt: 0, justifyContent: 'flex-end' }}>
                <IconButton
                  onClick={() => handleOpenDialog(customer)}
                  color="primary"
                  size="small"
                  sx={{
                    border: `1px solid ${alpha(theme.palette.primary.light, 0.4)}`,
                    borderRadius: 2,
                  }}
                >
                  <Edit />
                </IconButton>
                <IconButton
                  onClick={() => setDeleteConfirm(customer.id || null)}
                  color="error"
                  size="small"
                  sx={{
                    border: `1px solid ${alpha(theme.palette.error.light, 0.4)}`,
                    borderRadius: 2,
                  }}
                >
                  <Delete />
                </IconButton>
              </Stack>
            </Card>
        ))}
      </Box>

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: buildSurface(theme),
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editingId ? '거래처 수정' : '거래처 추가'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {/* 로고 업로드 */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
              <Avatar
                src={logoPreview}
                sx={{ width: 100, height: 100, border: `2px solid ${alpha(theme.palette.primary.light, 0.3)}` }}
              />
              <Button
                component="label"
                variant="outlined"
                startIcon={<PhotoCamera />}
                size="small"
              >
                로고 선택
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleLogoChange}
                />
              </Button>
            </Box>
            
            <TextField
              label="거래처명"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="구분"
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value as 'client' | 'partner' })}
              select
              required
              fullWidth
            >
              <MenuItem value="client">고객사</MenuItem>
              <MenuItem value="partner">협력사</MenuItem>
            </TextField>
            <TextField
              label="담당자"
              value={formData.contact}
              onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="연락처"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="이메일"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              fullWidth
            />
            <TextField
              label="주소"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              multiline
              rows={2}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setOpenDialog(false)}
            variant="outlined"
            color="secondary"
            sx={{
              px: 3,
              borderRadius: 999,
              fontWeight: 600,
              letterSpacing: '0.04em',
              borderColor: alpha(theme.palette.secondary.light, 0.6),
              color: theme.palette.secondary.light,
              '&:hover': {
                borderColor: theme.palette.secondary.light,
                backgroundColor: alpha(theme.palette.secondary.light, 0.12),
              },
            }}
          >
            취소
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{ px: 3, borderRadius: 999, fontWeight: 600, letterSpacing: '0.06em' }}
          >
            {editingId ? '수정' : '등록'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        PaperProps={{
          sx: buildSurface(theme),
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>거래처 삭제</DialogTitle>
        <DialogContent>
          <Typography>선택한 거래처를 삭제하시겠습니까?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setDeleteConfirm(null)}
            variant="outlined"
            color="secondary"
            sx={{
              px: 3,
              borderRadius: 999,
              fontWeight: 600,
              letterSpacing: '0.04em',
              borderColor: alpha(theme.palette.secondary.light, 0.6),
              color: theme.palette.secondary.light,
              '&:hover': {
                borderColor: theme.palette.secondary.light,
                backgroundColor: alpha(theme.palette.secondary.light, 0.12),
              },
            }}
          >
            취소
          </Button>
          <Button
            onClick={() => deleteConfirm && handleDelete(deleteConfirm)}
            color="error"
            variant="contained"
            sx={{ px: 3, borderRadius: 999, fontWeight: 600, letterSpacing: '0.06em' }}
          >
            삭제
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerManagement;
